import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { MapPin, Sparkles, X, Search } from 'lucide-react'
import {
  GoogleMap,
  Marker,
  Autocomplete,
} from '@react-google-maps/api'
import Errortext from '../../Utlis/Errortext'
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  CardLabel,
  FieldInput,
  PrimaryBtn,
  FieldHint,
  RequiredStar,
} from '../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles'

const LocationOverlay = styled(ModalOverlay)`
  z-index: 10050;
`

const LocationShell = styled(ModalShell)`
  max-width: 720px;
  max-height: 88vh;
`

const LocationBody = styled(ModalBody)`
  gap: 14px;
  padding-bottom: 20px;
`

const SearchCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  background: #fff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
`

const SearchFieldWrap = styled.div`
  position: relative;
  width: 100%;
`

const SearchIconWrap = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  color: rgba(23, 23, 23, 0.4);
  pointer-events: none;
  z-index: 1;
`

const SearchInput = styled(FieldInput)`
  padding-left: 42px;
`

const MapCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  flex: 1;
`

const MapHintRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MapHintIcon = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  background: linear-gradient(
    144.01deg,
    rgba(255, 201, 81, 0.8) 0%,
    rgba(206, 86, 176, 0.8) 100%
  );
`

const MapFrame = styled.div`
  position: relative;
  width: 100%;
  height: 340px;
  min-height: 240px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #f3f4f6;

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 220px;
  }
`

const SelectedAddressChip = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.08) 0%,
    rgba(206, 86, 176, 0.06) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.14);
`

const SelectedAddressIcon = styled.span`
  color: #c94daa;
  flex-shrink: 0;
  margin-top: 1px;
`

const SelectedAddressText = styled.p`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: #171717;
  word-break: break-word;
`

const SelectedAddressEmpty = styled(SelectedAddressText)`
  font-weight: 500;
  color: rgba(23, 23, 23, 0.5);
`

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-top: 2px;

  ${PrimaryBtn} {
    flex: 1;
    width: auto;
  }

  @media (max-width: 480px) {
    flex-direction: column-reverse;

    ${PrimaryBtn} {
      width: 100%;
    }
  }
`

const CancelBtn = styled.button`
  flex: 0 0 auto;
  min-width: 110px;
  height: 46px;
  padding: 0 20px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.65);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`

const mapContainerStyle = {
  height: '100%',
  width: '100%',
}

const center = {
  lat: 21.181119927085486,
  lng: 72.79392214369257,
}

interface LocationPickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (locationString: string) => void
}

const LocationPickerModal: React.FC<LocationPickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const [selectedPosition, setSelectedPosition] = useState<{
    lat: number
    lng: number
  } | null>({ lat: 21.181119927085486, lng: 72.79392214369257 })
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [showError, setShowError] = useState(false)

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat()
      const lng = event.latLng.lng()
      setSelectedPosition({ lat, lng })
      setShowError(false)

      const geocoder = new google.maps.Geocoder()
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          setInputValue(results[0].formatted_address)
        } else {
          setInputValue('Location not found')
        }
      })
    }
  }, [])

  const onLoadAutocomplete = (
    autocompleteInstance: google.maps.places.Autocomplete
  ) => {
    setAutocomplete(autocompleteInstance)
  }

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace()
      if (place.geometry) {
        const lat = place.geometry.location?.lat()
        const lng = place.geometry.location?.lng()
        if (lat && lng) {
          setSelectedPosition({ lat, lng })
          setInputValue(place.formatted_address || '')
          setShowError(false)
        }
      }
    }
  }

  const handleSelectLocation = () => {
    if (inputValue.trim().length === 0) {
      setShowError(true)
      return
    }
    onSelect(inputValue.trim())
  }

  if (!isOpen) return null

  return (
    <LocationOverlay onClick={onClose}>
      <LocationShell onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={10} />
                Venue
              </HeroBadge>
              <ModalTitle>Choose location</ModalTitle>
              <ModalSubtitle>
                Search an address or tap the map to pin the venue for this day.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <LocationBody>
          <SearchCard>
            <CardLabel htmlFor="location-search">
              Search address <RequiredStar>*</RequiredStar>
            </CardLabel>
            <FieldHint>
              Start typing a place name, then pick a suggestion — or click the map below.
            </FieldHint>
            <SearchFieldWrap>
              <SearchIconWrap>
                <Search size={16} />
              </SearchIconWrap>
              <Autocomplete
                onLoad={onLoadAutocomplete}
                onPlaceChanged={onPlaceChanged}
              >
                <SearchInput
                  id="location-search"
                  value={inputValue}
                  type="text"
                  placeholder="e.g. Hotel name, street, or area"
                  onChange={(e) => {
                    setInputValue(e.target.value)
                    if (e.target.value.trim()) setShowError(false)
                  }}
                />
              </Autocomplete>
            </SearchFieldWrap>
            <Errortext
              show={showError && inputValue.trim().length === 0}
              message="Please search or tap the map to set a location."
            />
          </SearchCard>

          <MapCard>
            <MapHintRow>
              <MapHintIcon>
                <MapPin size={14} />
              </MapHintIcon>
              <FieldHint>
                Tap anywhere on the map to drop a pin. The address updates automatically.
              </FieldHint>
            </MapHintRow>
            <MapFrame>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={selectedPosition || center}
                zoom={14}
                onClick={handleMapClick}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                {selectedPosition && <Marker position={selectedPosition} />}
              </GoogleMap>
            </MapFrame>
          </MapCard>

          <SelectedAddressChip>
            <SelectedAddressIcon>
              <MapPin size={16} />
            </SelectedAddressIcon>
            {inputValue.trim() ? (
              <SelectedAddressText>{inputValue}</SelectedAddressText>
            ) : (
              <SelectedAddressEmpty>
                No address selected yet — search above or tap the map.
              </SelectedAddressEmpty>
            )}
          </SelectedAddressChip>

          <ActionsRow>
            <CancelBtn type="button" onClick={onClose}>
              Cancel
            </CancelBtn>
            <PrimaryBtn type="button" onClick={handleSelectLocation}>
              Use this location
            </PrimaryBtn>
          </ActionsRow>
        </LocationBody>
      </LocationShell>
    </LocationOverlay>
  )
}

export default LocationPickerModal
